import PaymentMethod from '../../models/PaymentMethod';
import { formatPaymentMethods } from '../../helpers/card.helper';

const namespaced = true;

const getInitialState = () => {
    return {
        paymentMethods: [],
        clientSecret: {},
        selectedPaymentMethod: null,
        upcomingInvoicePreview: null,
        hasUpcomingInvoicePreview: false,
        isUpcomingInvoiceRefreshing: false
    };
};

const state = getInitialState();

const getters = {
    paymentMethods: state => state.paymentMethods,
    clientSecret: state => state.clientSecret,
    selectedPaymentMethod: state => state.selectedPaymentMethod,
    upcomingInvoicePreview: state => state.upcomingInvoicePreview,
    isUpcomingInvoiceLoading: state => state.isUpcomingInvoiceRefreshing
        && !state.hasUpcomingInvoicePreview
};

const mutations = {
    SET_UPCOMING_INVOICE: (state, invoice) => {
        state.upcomingInvoicePreview = invoice;
        state.hasUpcomingInvoicePreview = true;
    },
    SET_UPCOMING_INVOICE_REFRESHING: (state, refreshing) => {
        state.isUpcomingInvoiceRefreshing = refreshing;
    },
    SET_CLIENT_SECRET: (state, payload) => {
        state.clientSecret = payload;
    },
    SET_SELECTED_PAYMENT_METHOD: (state, payload) => {
        state.selectedPaymentMethod = payload;
    },
    REMOVE_SELECTED_PAYMENT_METHOD: (state) => {
        state.selectedPaymentMethod = null;
    },
    SET_PAYMENT_METHODS: (state, payload) => {
        state.paymentMethods = formatPaymentMethods(payload).map(pm => new PaymentMethod(pm));
    },
    ADD_PAYMENT_METHOD: (state, payload) => {
        state.paymentMethods = formatPaymentMethods([
            ...state.paymentMethods,
            payload
        ]).map(paymentMethod => new PaymentMethod(paymentMethod));
    },
    REMOVE_PAYMENT_METHOD_BY_ID: (state, payload) => {
        state.paymentMethods = state.paymentMethods.filter(paymentMethod => paymentMethod.id !== payload);
    },
    UPDATE_PAYMENT_METHOD: (state, payload) => {
        const paymentMethodIndex = state.paymentMethods.findIndex(paymentMethod => paymentMethod.id === payload.id);

        state.paymentMethods[paymentMethodIndex] = payload;

        state.paymentMethods = [ ...state.paymentMethods ];
    },
    RESET_STATE: state => {
        Object.assign(state, getInitialState());
    }
};

const actions = {
    GET_UPCOMING_INVOICE: async context => {
        context.commit('SET_UPCOMING_INVOICE_REFRESHING', true);
        try {
            const response = await window.api.call('get-upcoming-invoice');
            const invoice = response.data.data || null;

            context.commit('SET_UPCOMING_INVOICE', invoice);

            return invoice;
        } catch (error) {
            console.error('Error fetching upcoming invoice:', error);
        } finally {
            context.commit('SET_UPCOMING_INVOICE_REFRESHING', false);
        }
    },
    GET_CLIENT_SECRET: async context => {
        try {
            const response = await window.api.call('setup-intent-client-secret');

            context.commit('SET_CLIENT_SECRET', response.data.data);

            return response.data.data;
        } catch (error) {
            console.error('Error fetching client secret:', error);
            throw error;
        }
    },
    CREATE_PAYMENT_METHOD: async (context, { payment_method, is_default }) => {
        const response = await window.api.call('create-payment-methods', {
            payment_method,
            is_default
        });

        context.commit('SET_SELECTED_PAYMENT_METHOD', response.data.data);
        context.commit('ADD_PAYMENT_METHOD', response.data.data);

        return response.data.data;
    },

    GET_USER_PAYMENT_METHODS: async context => {
        try {
            const response = await window.api.call('get-payment-methods');
            const methods = response.data.data || [];

            context.commit('SET_PAYMENT_METHODS', methods);

            const selected = context.state.selectedPaymentMethod;
            const hasSelected = !!selected?.id;
            const selectedStillExists = hasSelected
                ? context.state.paymentMethods.some(paymentMethod => paymentMethod.id === selected.id)
                : false;

            if (!selectedStillExists) {
                const primary =
                    context.state.paymentMethods.find(paymentMethod => paymentMethod?.is_default) ||
                    context.state.paymentMethods[0] ||
                    null;

                context.commit('SET_SELECTED_PAYMENT_METHOD', primary);
            }

            return methods;
        } catch (error) {
            console.error('Error fetching user payment methods:', error);

        }
    },
    DELETE_PAYMENT_METHOD: async (context, payload) => {
        const response = await window.api.call('delete-payment-method', { id: payload });

        context.commit('REMOVE_PAYMENT_METHOD_BY_ID', payload);

        return response.data.data;
    }
};

export default {
    namespaced,
    state,
    mutations,
    actions,
    getters
};
